import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quoteSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  freight_type: z.string().min(1, 'Please select a freight type'),
  weight: z.string().min(1, 'Weight is required'),
  pickup_zip: z.string().regex(/^\d{5}$/, 'Invalid ZIP code'),
  delivery_zip: z.string().regex(/^\d{5}$/, 'Invalid ZIP code'),
  accessorials: z.array(z.string()).optional(),
  description: z.string().min(10, 'Please provide more details about your freight'),
});

const freightTypes = [
  { value: 'palletized', label: 'Palletized Freight' },
  { value: 'ltl', label: 'LTL (Less Than Truckload)' },
  { value: 'ftl', label: 'FTL (Full Truckload)' },
  { value: 'oversized', label: 'Oversized Load' },
  { value: 'equipment', label: 'Equipment / Heavy Machinery' },
  { value: 'other', label: 'Other' },
];

const accessorialOptions = [
  { id: 'liftgate_pickup', label: 'Liftgate at Pickup' },
  { id: 'liftgate_delivery', label: 'Liftgate at Delivery' },
  { id: 'residential_pickup', label: 'Residential Pickup' },
  { id: 'residential_delivery', label: 'Residential Delivery' },
  { id: 'appointment_pickup', label: 'Appointment Required (Pickup)' },
  { id: 'appointment_delivery', label: 'Appointment Required (Delivery)' },
  { id: 'inside_pickup', label: 'Inside Pickup' },
  { id: 'inside_delivery', label: 'Inside Delivery' },
  { id: 'hazmat', label: 'Hazmat' },
  { id: 'tarping', label: 'Tarping Required' },
];

const steps = [
  { id: 1, title: 'Contact Info' },
  { id: 2, title: 'Freight Details' },
  { id: 3, title: 'Route & Notes' },
];

export const QuoteWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedAccessorials, setSelectedAccessorials] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      freight_type: '',
      weight: '',
      pickup_zip: '',
      delivery_zip: '',
      accessorials: [],
      description: '',
    },
  });

  const watchFreightType = watch('freight_type');

  const handleAccessorialChange = (accessorialId, checked) => {
    const updated = checked
      ? [...selectedAccessorials, accessorialId]
      : selectedAccessorials.filter((id) => id !== accessorialId);
    setSelectedAccessorials(updated);
    setValue('accessorials', updated);
  };

  const nextStep = async () => {
    let fieldsToValidate = [];
    if (currentStep === 1) fieldsToValidate = ['name', 'email', 'phone'];
    if (currentStep === 2) fieldsToValidate = ['freight_type', 'weight'];

    const isValid = await trigger(fieldsToValidate);
    if (isValid) setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await axios.post(`${API}/quotes`, {
        ...data,
        accessorials: selectedAccessorials,
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Quote submission error:', error);
      setSubmitError('Failed to submit quote. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
        data-testid="quote-success-message"
      >
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-white" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-navy-900 mb-4">
          Quote Request Received!
        </h2>
        <p className="font-body text-slate-600 max-w-md mx-auto mb-8">
          Thank you for your request. One of our freight specialists will review your details and follow up with pricing shortly.
        </p>
        <p className="font-body text-sm text-slate-500">
          Questions? Call us at{' '}
          <a href="tel:+18005551234" className="text-orange-500 font-medium">
            (800) 555-1234
          </a>
        </p>
      </motion.div>
    );
  }

  return (
    <div className="w-full" data-testid="quote-wizard">
      {/* Progress Bar */}
      <div className="mb-10">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-body font-semibold text-sm transition-colors duration-300 ${
                    currentStep >= step.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                </div>
                <span
                  className={`mt-2 font-body text-xs font-medium ${
                    currentStep >= step.id ? 'text-navy-900' : 'text-slate-400'
                  }`}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-16 sm:w-24 h-1 mx-2 sm:mx-4 rounded transition-colors duration-300 ${
                    currentStep > step.id ? 'bg-orange-500' : 'bg-slate-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Steps */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <AnimatePresence mode="wait">
          {/* Step 1: Contact Info */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div>
                <Label htmlFor="name" className="font-body font-medium text-navy-900">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="John Smith"
                  className="mt-2"
                  data-testid="input-name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email" className="font-body font-medium text-navy-900">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="john@company.com"
                  className="mt-2"
                  data-testid="input-email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="font-body font-medium text-navy-900">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  {...register('phone')}
                  placeholder="(555) 123-4567"
                  className="mt-2"
                  data-testid="input-phone"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 2: Freight Details */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div>
                <Label className="font-body font-medium text-navy-900">Freight Type *</Label>
                <Select
                  value={watchFreightType}
                  onValueChange={(value) => setValue('freight_type', value)}
                >
                  <SelectTrigger className="mt-2" data-testid="select-freight-type">
                    <SelectValue placeholder="Select freight type" />
                  </SelectTrigger>
                  <SelectContent>
                    {freightTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.freight_type && (
                  <p className="mt-1 text-sm text-red-500">{errors.freight_type.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="weight" className="font-body font-medium text-navy-900">
                  Estimated Weight (lbs) *
                </Label>
                <Input
                  id="weight"
                  {...register('weight')}
                  placeholder="e.g., 5000 lbs"
                  className="mt-2"
                  data-testid="input-weight"
                />
                {errors.weight && (
                  <p className="mt-1 text-sm text-red-500">{errors.weight.message}</p>
                )}
              </div>

              <div>
                <Label className="font-body font-medium text-navy-900 mb-3 block">
                  Accessorials (Optional)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {accessorialOptions.map((option) => (
                    <div key={option.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={option.id}
                        checked={selectedAccessorials.includes(option.id)}
                        onCheckedChange={(checked) =>
                          handleAccessorialChange(option.id, checked)
                        }
                        data-testid={`checkbox-${option.id}`}
                      />
                      <label
                        htmlFor={option.id}
                        className="font-body text-sm text-slate-600 cursor-pointer"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Route & Notes */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pickup_zip" className="font-body font-medium text-navy-900">
                    Pickup ZIP *
                  </Label>
                  <Input
                    id="pickup_zip"
                    {...register('pickup_zip')}
                    placeholder="12345"
                    className="mt-2"
                    data-testid="input-pickup-zip"
                  />
                  {errors.pickup_zip && (
                    <p className="mt-1 text-sm text-red-500">{errors.pickup_zip.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="delivery_zip" className="font-body font-medium text-navy-900">
                    Delivery ZIP *
                  </Label>
                  <Input
                    id="delivery_zip"
                    {...register('delivery_zip')}
                    placeholder="67890"
                    className="mt-2"
                    data-testid="input-delivery-zip"
                  />
                  {errors.delivery_zip && (
                    <p className="mt-1 text-sm text-red-500">{errors.delivery_zip.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="font-body font-medium text-navy-900">
                  Freight Description *
                </Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Describe your freight: dimensions, special handling requirements, timeline, etc."
                  className="mt-2 min-h-[120px]"
                  data-testid="input-description"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-body text-sm text-red-600">{submitError}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="mt-10 flex justify-between">
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              className="font-body font-semibold"
              data-testid="btn-prev-step"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back
            </Button>
          ) : (
            <div />
          )}

          {currentStep < 3 ? (
            <Button
              type="button"
              onClick={nextStep}
              className="bg-orange-500 hover:bg-orange-600 text-white font-body font-semibold"
              data-testid="btn-next-step"
            >
              Continue
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-orange-500 hover:bg-orange-600 text-white font-body font-semibold"
              data-testid="btn-submit-quote"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Request
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
